import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetReduxState } from '../actions';
import { saveSelectedFiscalYear, getSelectedFiscalYear } from '../../utils/storageUtils';

export interface AnnualReport {
  annualReportId: string;
  financialStatementId: string;
  year: number;
  materiality: number;
  fiscalYearEndDate: string; // ISO date string e.g., "2026-04-01T06:00:00.000Z"
  materialityDescription: string | null;
}

interface AnnualReportState {
  reports: AnnualReport[];
  selectedReport: AnnualReport | null;
}

const initialState: AnnualReportState = {
  reports: [],
  selectedReport: null,
};

const annualReportSlice = createSlice({
  name: 'annualReport',
  initialState,
  reducers: {
    setReports: (state, action: PayloadAction<AnnualReport[]>) => {
      state.reports = action.payload;
      if (!state.selectedReport && action.payload.length > 0) {
        const savedYear = getSelectedFiscalYear();
        const savedReport = savedYear != null
          ? action.payload.find(r => r.year === savedYear)
          : null;
        state.selectedReport = savedReport ?? action.payload[0];
      }
    },
    setSelectedReport: (state, action: PayloadAction<AnnualReport>) => {
      state.selectedReport = action.payload;
      saveSelectedFiscalYear(action.payload.year);
    },
    setSelectedReportByYear: (state, action: PayloadAction<number>) => {
      const report = state.reports.find(r => r.year === action.payload);
      if (report) {
        state.selectedReport = report;
        saveSelectedFiscalYear(report.year);
      }
    },
    setMateriality: (state, action: PayloadAction<number>) => {
      if (state.selectedReport) {
        state.selectedReport.materiality = action.payload;
      }
      state.reports = state.reports.map(r => r.annualReportId === state.selectedReport?.annualReportId ? { ...r, materiality: action.payload } : r);
    },
    setMaterialityDescription: (state, action: PayloadAction<string | null>) => {
      if (state.selectedReport) {
        state.selectedReport.materialityDescription = action.payload;
      }
      state.reports = state.reports.map(r =>
        r.annualReportId === state.selectedReport?.annualReportId
          ? { ...r, materialityDescription: action.payload }
          : r
      );
    },
    setFiscalYearEndDate: (state, action: PayloadAction<{ annualReportId: string; fiscalYearEndDate: string }>) => {
      const { annualReportId, fiscalYearEndDate } = action.payload;
      if (state.selectedReport?.annualReportId === annualReportId) {
        state.selectedReport.fiscalYearEndDate = fiscalYearEndDate;
      }
      state.reports = state.reports.map(r =>
        r.annualReportId === annualReportId ? { ...r, fiscalYearEndDate } : r
      );
    },
    clearAnnualReports: (state) => {
      state.reports = [];
      state.selectedReport = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

export const {
  setReports,
  setSelectedReport,
  setSelectedReportByYear,
  setMateriality,
  setMaterialityDescription,
  setFiscalYearEndDate,
  clearAnnualReports,
} = annualReportSlice.actions;

export default annualReportSlice.reducer;

